import { useAuthStore } from "../stores/authStore";
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  Zap, Bookmark,
  Brain,
  Send,
  CheckCircle2,
  Loader2,
  Layers,
  Wand2,
  Pencil,
  X,
  FileCode,
  Eye,
  Bug,
  AlertTriangle,
  Info,
  ChevronRight,
  ChevronDown,
  Maximize2,
  GanttChart,
  GitBranch,
  FileJson,
  Braces,
  RefreshCw,
  ExternalLink,
  Play, Paperclip, Globe, Blocks, Mic, Video, Square, FileText, Plus, Sparkles,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/services/client';
import { chat as llmChatWs } from '@/services/api-client';
import { tabService } from '@/services/tabs';
import { appService } from '@/services/apps';
import type { ApiBrowserTab, AppType } from '@/types';
import { PageLayout } from '@/components/layout/PageLayout';
import { EmbeddedSkillRail } from '@/components/skill';
import { HitlPromptCard } from '@/components/apps/HitlPromptCard';
import { BuildConsole } from '@/components/apps/BuildConsole';
import { FileViewerModal } from '@/components/apps/FileViewerModal';
import { DeliverablesTree } from '@/components/apps/DeliverablesTree';

const WS_SESSION_STORAGE_KEY = 'khiimori_workspace_session_id';

interface BuildLog {
  text: string;
  ts: number;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  suggestion?: AppSuggestion;
  suggestions?: AppSuggestion[];
  hitlPromptData?: { resume_token?: string, user_instruction?: string, question?: string, explanation?: string, context?: any, questions?: any[], buttons?: any[], options?: string[], option_descriptions?: string[] };
}

interface AppSuggestion {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  tabCount: number;
  confidence: number;
  features: string[];
  appType: AppType;
}

interface GenerationTask {
  id: string;
  name: string;
  topic: string;
  progress: number;
  status: 'building' | 'ready' | 'complete';
  timestamp: Date;
  appId?: string;
}

interface AppPlan {
  id: string;
  app_name: string;
  summary: string;
  target_users: string;
  core_features: string[];
  programming_languages?: string[];
  dependencies?: string[];
  component_tree: PlannedComponent[];
  data_flow: { data_sources: string[]; data_models: string[]; state_shape: string; data_flow_notes: string };
  route_design: PlannedRoute[];
  state_management: string;
  api_integrations: string[];
  ui_library: string;
  design_rationale: string;
  estimated_complexity: string;
  generated_at: string;
}

interface PlannedComponent {
  name: string;
  type: string;
  purpose: string;
  props: string[];
  children: string[];
  has_state: boolean;
  api_dependent: boolean;
}

interface PlannedRoute {
  path: string;
  component: string;
  layout: string;
  description: string;
}

interface CritiqueResult {
  overall_score: number;
  strengths: string[];
  issues: CritiqueIssue[];
  suggestions: string[];
  would_auto_fix: boolean;
  auto_fix_description?: string;
}

interface CritiqueIssue {
  severity: string;
  category: string;
  description: string;
  location: string;
}

interface GeneratedFile {
  path: string;
  content: string;
  language: string;
  description: string;
  category: string;
}

export function chatAssistantText(payload: unknown): string {
  if (payload == null) return '';
  if (typeof payload === 'string') return payload;
  if (typeof payload === 'object') {
    const o = payload as Record<string, unknown>;
    if (typeof o.content === 'string') return o.content;
    const choices = o.choices;
    if (Array.isArray(choices) && choices.length > 0) {
      const first = choices[0] as Record<string, unknown>;
      const msg = first.message;
      if (msg && typeof msg === 'object') {
        const c = (msg as Record<string, unknown>).content;
        if (typeof c === 'string') return c;
      }
    }
    const inner = o.message;
    if (inner && typeof inner === 'object') {
      const c = (inner as Record<string, unknown>).content;
      if (typeof c === 'string') return c;
    }
  }
  return '';
}

function mapServerSuggestion(
  s: { type: string; confidence: number; reason: string; name?: string; description?: string },
  tabCount: number,
  idx: number
): AppSuggestion {
  const colors = ['indigo', 'purple', 'green', 'amber'];
  const allowed: AppType[] = ['productivity', 'social', 'e-commerce', 'blog', 'portfolio', 'dashboard', 'custom'];
  const t = (allowed.includes(s.type as AppType) ? s.type : 'custom') as AppType;
  
  let IconComponent = <Sparkles color="#8b5cf6" size={24} />;
  switch (t) {
    case 'productivity': IconComponent = <CheckCircle2 color="#4ade80" size={24} />; break;
    case 'social': IconComponent = <Globe color="#60a5fa" size={24} />; break;
    case 'e-commerce': IconComponent = <Zap color="#fbbf24" size={24} />; break;
    case 'blog': IconComponent = <FileText color="#f472b6" size={24} />; break;
    case 'portfolio': IconComponent = <Layers color="#c084fc" size={24} />; break;
    case 'dashboard': IconComponent = <Blocks color="#22d3ee" size={24} />; break;
  }

  return {
    id: `sug-${idx}-${s.type}`,
    name: s.name || `${s.type.replace(/_/g, ' ')} workspace`,
    description: s.description || s.reason,
    icon: IconComponent,
    color: colors[idx % colors.length],
    tabCount,
    confidence: Math.min(100, Math.round((s.confidence <= 1 ? s.confidence * 100 : s.confidence))),
    features: [],
    appType: t,
  };
}

function buildFallbackSuggestion(prompt: string, tabCount: number): AppSuggestion {
  const cleanPrompt = prompt.trim();
  const nameBase = cleanPrompt.length > 48 ? `${cleanPrompt.slice(0, 48)}...` : cleanPrompt;
  return {
    id: `fallback-${Date.now()}`,
    name: nameBase ? `${nameBase} workspace` : 'Custom workspace',
    description:
      'I can generate a workspace from your current tabs now, even without a specific suggestion type from analysis.',
    icon: <Sparkles color="#8b5cf6" size={24} />,
    color: 'indigo',
    tabCount,
    confidence: 70,
    features: [],
    appType: 'custom',
  };
}

export function AppGenerator() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeHitlPrompt, setActiveHitlPrompt] = useState<any>(null);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [openTabs, setOpenTabs] = useState<ApiBrowserTab[]>([]);
  const [workspaceSessionId, setWorkspaceSessionId] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [buildingLogs, setBuildingLogs] = useState<BuildLog[]>([]);
  const [generationError, setGenerationError] = useState<string | null>(null);
  // Append a timestamped log line (collapses immediate duplicates, keeps the last 200).
  const appendLog = useCallback((text?: string) => {
    if (!text) return;
    setBuildingLogs((prev) =>
      prev.length && prev[prev.length - 1].text === text ? prev : [...prev.slice(-199), { text, ts: Date.now() }],
    );
  }, []);
  // Progress only moves forward while a run is active (explicit resets still use setGenerationProgress).
  const bumpProgress = useCallback((pct?: number) => {
    if (pct === undefined || pct === null || Number.isNaN(pct)) return;
    setGenerationProgress((prev) => Math.max(prev, Math.min(100, Math.round(pct))));
  }, []);
  const [activeTask, setActiveTask] = useState<GenerationTask | null>(null);
  const [completedTasks, setCompletedTasks] = useState<GenerationTask[]>([]);
  const [suggestionNotice, setSuggestionNotice] = useState<string | null>(null);
  const [nameDialogSuggestion, setNameDialogSuggestion] = useState<AppSuggestion | null>(null);
  const [customName, setCustomName] = useState('');
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [appPlan, setAppPlan] = useState<AppPlan | null>(null);
  const [planStage, setPlanStage] = useState<'none' | 'planning' | 'review' | 'approved'>('none');
  const [pipelineStage, setPipelineStage] = useState<'idle' | 'planning' | 'generating' | 'critiquing' | 'fixing' | 'complete'>('idle');
  const pipelineStageRef = useRef(pipelineStage);
  pipelineStageRef.current = pipelineStage;
  const [generatedFiles, setGeneratedFiles] = useState<GeneratedFile[]>([]);
  const [critiqueResult, setCritiqueResult] = useState<CritiqueResult | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [deployStatus, setDeployStatus] = useState<'idle' | 'deploying' | 'done' | 'error'>('idle');
  const [cdnUrl, setCdnUrl] = useState<string | null>(null);
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);
  const [componentTreeExpanded, setComponentTreeExpanded] = useState(true);
  const [prompt, setPrompt] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const loadTabs = useCallback(async (sessionId: string) => {
    try {
      const { tabs } = await tabService.getTabsBySession(sessionId, 1, 100);
      setOpenTabs(tabs);
      return tabs;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load tabs');
      setOpenTabs([]);
      return [];
    }
  }, []);


  const fetchSuggestions = async (sessionId: string, tabIds: string[]) => {
    if (tabIds.length === 0) return [];
    try {
      const res = await api.get<{ suggestions: { type: string; confidence: number; reason: string; name?: string; description?: string }[] }>(`/sessions/${sessionId}/tabs/suggestions?tab_ids=${tabIds.join(',')}`);
      return (res.data.suggestions || []).map((s, idx) => mapServerSuggestion(s, tabIds.length, idx));
    } catch {
      return [];
    }
  };

  const resolveWorkspaceSession = useCallback(async () => {
    setSessionLoading(true);
    try {
      const cached = sessionStorage.getItem(WS_SESSION_STORAGE_KEY);
      if (cached) {
        setWorkspaceSessionId(cached);
        const tabs = await loadTabs(cached);
        const tabIds = tabs.map(t => t.id);
        let suggestions = await fetchSuggestions(cached, tabIds);
        if (suggestions.length === 0 && tabs.length > 0) {
          suggestions = [buildFallbackSuggestion('', tabs.length)];
        }
        setMessages([
          {
            id: 'welcome',
            role: 'assistant',
            content: `Welcome to App Forge. You have ${tabs.length} tab(s) in this workspace. Describe what you want to build and I will help; I can suggest a workspace type from your open tabs.`,
            timestamp: new Date(),
            suggestions: suggestions,
          },
        ]);
        setSessionLoading(false);
        return;
      }
      const cur = await api.get<{ session: { id: string } }>('/sessions/current');
      const id = cur.data.session.id;
      sessionStorage.setItem(WS_SESSION_STORAGE_KEY, id);
      setWorkspaceSessionId(id);
      const tabs = await loadTabs(id);
      setMessages([
        {
          id: 'welcome',
          role: 'assistant',
          content: `Welcome to App Forge. You have ${tabs.length} tab(s) in this workspace. Describe what you want to build and I will help; I can suggest a workspace type from your open tabs.`,
          timestamp: new Date(),
        },
      ]);
    } catch {
      try {
        const list = await api.get<{ sessions: { id: string }[] }>('/sessions');
        const first = list.data.sessions?.[0]?.id;
        if (first) {
          sessionStorage.setItem(WS_SESSION_STORAGE_KEY, first);
          setWorkspaceSessionId(first);
          const tabs = await loadTabs(first);
          const tabIds = tabs.map(t => t.id);
          const suggestions = await fetchSuggestions(first, tabIds);
          setMessages([
            {
              id: 'welcome',
              role: 'assistant',
              content: `Welcome to App Forge. You have ${tabs.length} tab(s) in this workspace. Describe what you want to build and I will help; I can suggest a workspace type from your open tabs.`,
              timestamp: new Date(),
              suggestions: suggestions,
            },
          ]);
        } else {
          const created = await api.post<{ id: string }>('/sessions', { name: 'App Forge' });
          const id = (created.data as { id?: string }).id;
          if (id) {
            sessionStorage.setItem(WS_SESSION_STORAGE_KEY, id);
            setWorkspaceSessionId(id);
            const tabs = await loadTabs(id);
            const tabIds = tabs.map(t => t.id);
            let suggestions = await fetchSuggestions(id, tabIds);
            if (suggestions.length === 0 && tabs.length > 0) {
              suggestions = [buildFallbackSuggestion('', tabs.length)];
            }
            setMessages([
              {
                id: 'welcome',
                role: 'assistant',
                content: `Welcome to App Forge. You have ${tabs.length} tab(s) in this workspace. Describe what you want to build and I will help; I can suggest a workspace type from your open tabs.`,
                timestamp: new Date(),
                suggestions: suggestions,
              },
            ]);
          }
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Sign in required to use App Forge');
        setWorkspaceSessionId(null);
      }
    } finally {
      setSessionLoading(false);
    }
  }, [loadTabs]);

  useEffect(() => {
    void resolveWorkspaceSession();
  }, [resolveWorkspaceSession]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (seedPrompt?: string) => {
    const prompt = (seedPrompt ?? inputValue).trim();
    if (!prompt || !workspaceSessionId) return;
    console.log('[AppForge] handleSendMessage start | prompt:', prompt.slice(0, 120), '| session:', workspaceSessionId, '| tabs:', openTabs.length);
    setSuggestionNotice(null);

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: prompt,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    const tabLines = openTabs
      .map((t) => `- ${t.title || '(untitled)'}: ${t.url}`)
      .join('\n');
    const system = `You help the user design a small web workspace from their browser tabs. Open tabs:\n${tabLines || '(none)'}\nBe concise and practical.`;

    try {
      const res = await llmChatWs(
        [
          { role: 'system', content: system },
          { role: 'user', content: prompt },
        ],
        {
          provider: 'openai',
          max_tokens: 1024,
          temperature: 0.6,
        }
      );
      const text = chatAssistantText(res);
      console.log('[AppForge] LLM chat response received | text length:', text?.length ?? 0);
      let suggestion: AppSuggestion | undefined;
      try {
        const tabIds = openTabs.map((t) => t.id);
        console.log('[AppForge] fetching suggestions | session:', workspaceSessionId, '| tabIDs:', tabIds);
        const sug = await appService.getSuggestedAppTypes(workspaceSessionId, tabIds);
        console.log('[AppForge] suggestions returned | count:', sug.length, '| first:', sug[0] ?? 'none');
        if (sug.length > 0) {
          suggestion = mapServerSuggestion(sug[0], openTabs.length, 0);
          console.log('[AppForge] using server suggestion | type:', sug[0].type, '| confidence:', sug[0].confidence);
        } else if (openTabs.length > 0) {
          suggestion = buildFallbackSuggestion(prompt, openTabs.length);
          console.log('[AppForge] no suggestions — built fallback | name:', suggestion.name);
          setSuggestionNotice('No app type suggestions were returned, so a custom workspace option is ready below.');
        }
      } catch (e) {
        console.warn('[AppForge] suggestion fetch failed:', e);
        if (openTabs.length > 0) {
          suggestion = buildFallbackSuggestion(prompt, openTabs.length);
          setSuggestionNotice('Suggestion service is temporarily unavailable, but you can still generate a custom workspace.');
        }
      }
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: text || 'I could not generate a reply. Check that an LLM provider is configured on the server.',
        timestamp: new Date(),
        suggestion,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Chat request failed';
      console.error('[AppForge] LLM chat request failed:', msg);
      toast.error(msg);
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content:
            msg.includes('503') || msg.toLowerCase().includes('llm')
              ? 'The LLM service is not available. Configure providers in the server environment, then try again.'
              : msg,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSaveWorkspace = (suggestion: AppSuggestion) => {
    setNameDialogSuggestion(suggestion);
    setCustomName(suggestion.name);
    setNameDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!nameDialogSuggestion) return;
    const nameToUse = customName.trim() || nameDialogSuggestion.name;
    const finalSuggestion = { ...nameDialogSuggestion, name: nameToUse };
    setNameDialogOpen(false);
    setNameDialogSuggestion(null);
    await handleGenerateApp(finalSuggestion);
  };

  const handleGenerateApp = async (suggestion: AppSuggestion) => {
    console.log('[AppForge] handleGenerateApp start | name:', suggestion.name, '| type:', suggestion.appType);
    if (!workspaceSessionId) {
      toast.error('No workspace session');
      return;
    }
    const tabIds = openTabs.map((t) => t.id);
    if (tabIds.length === 0) {
      toast.error('Add or sync tabs in this workspace before generating.');
      return;
    }

    const newTask: GenerationTask = {
      id: Date.now().toString(),
      name: suggestion.name,
      topic: suggestion.description.slice(0, 80) + (suggestion.description.length > 80 ? '…' : ''),
      progress: 0,
      status: 'building',
      timestamp: new Date(),
    };
    setActiveTask(newTask);
    setIsGenerating(true);
    setGenerationProgress(0);
    setBuildingLogs([]);
    setGenerationError(null);
    setPlanStage('approved');
    setPipelineStage('generating');

    // Clean up previous EventSource if any
    const prevSrc = (window as unknown as Record<string, EventSource | undefined>)['__appGenSrc'];
    if (prevSrc) {
      prevSrc.close();
    }

    try {
      console.log('[AppForge] SSE /sessions/:id/apps/generate/stream | session:', workspaceSessionId, '| tabIDs:', tabIds, '| appType:', suggestion.appType);
      const es = tabService.generateAppFromTabsStream(workspaceSessionId, {
        tab_ids: tabIds,
        app_type: suggestion.appType,
        app_name: suggestion.name,
        description: suggestion.description,
      });
      (window as unknown as Record<string, EventSource | undefined>)['__appGenSrc'] = es;

      let idStr = '';

      es.addEventListener('hitl_prompt', (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.question && !data.user_instruction) {
            data.user_instruction = data.question;
          }
          setActiveHitlPrompt(data);
        } catch { /* ignore */ }
      });

      es.addEventListener('file_generated', (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.path) appendLog(`   ↳ received ${data.path}`);
          setGeneratedFiles((prev) => {
            const existingIdx = prev.findIndex(f => f.path === data.path);
            if (existingIdx !== -1) {
              const next = [...prev];
              next[existingIdx] = data;
              return next;
            }
            return [...prev, data];
          });
        } catch { /* ignore */ }
      });

      es.addEventListener('building', (e) => {
        try {
          const data = JSON.parse(e.data);
          bumpProgress(data.percent);
          appendLog(data.message);
        } catch { /* ignore parse errors */ }
      });

      es.addEventListener('generation_progress', (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.stage !== 'error') bumpProgress(data.percent ?? data.progress);
          if (data.stage === 'error') appendLog(`✗ ${data.message}`);
          // Khiimori's own pipeline stages (fetching tabs, saving, S3 …); Genesis
          // steps arrive separately as 'building' events.
          else if (data.stage !== 'building' && data.message) appendLog(`• ${data.message}`);
        } catch { /* ignore parse errors */ }
      });

      es.addEventListener('generation_completed', (e) => {
        try {
          const data = JSON.parse(e.data);
          idStr = String(data.app_id ?? '');
          if (data.preview_url) {
            setPreviewUrl(data.preview_url);
          }
          setGenerationProgress(100);
          setPipelineStage('complete');
          setActiveTask((prev) =>
            prev
              ? { ...prev, progress: 100, status: 'ready', appId: idStr }
              : null,
          );
          const successMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: `Your workspace "${suggestion.name}" has been generated. Open it below.`,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, successMessage]);
          setTimeout(() => {
            setActiveTask((prev) => {
              if (prev) {
                setCompletedTasks((c) => [{ ...prev, status: 'complete', appId: idStr }, ...c]);
              }
              return null;
            });
            setIsGenerating(false);
            setGenerationProgress(0);
          }, 2000);
        } catch { /* ignore parse errors */ }
        es.close();
        delete (window as unknown as Record<string, EventSource | undefined>)['__appGenSrc'];
      });

      es.addEventListener('generation_failed', (e) => {
        try {
          const data = JSON.parse(e.data);
          const msg = data.error ?? 'Generation failed';
          toast.error(msg);
          setGenerationError(msg);
          appendLog(`✗ ${msg}`);
        } catch {
          toast.error('Generation failed');
          setGenerationError('Generation failed');
        }
        setActiveTask(null);
        setIsGenerating(false);
        es.close();
        delete (window as unknown as Record<string, EventSource | undefined>)['__appGenSrc'];
      });

      es.onerror = () => {
        toast.error('Connection to generation service lost');
        setGenerationError('Connection to the generation service was lost.');
        appendLog('✗ Connection to generation service lost');
        setActiveTask(null);
        setIsGenerating(false);
        es.close();
        delete (window as unknown as Record<string, EventSource | undefined>)['__appGenSrc'];
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Generation failed';
      toast.error(msg);
      setActiveTask(null);
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const handleGenerateWithPlanning = async (suggestion: AppSuggestion) => {
    if (!workspaceSessionId) return;
    const tabIds = openTabs.map((t) => t.id);
    if (tabIds.length === 0) {
      toast.error('Add or sync tabs in this workspace before generating.');
      return;
    }

    setPlanStage('planning');
    setPipelineStage('planning');
    setAppPlan(null);
    setGeneratedFiles([]);
    setCritiqueResult(null);
    setPreviewUrl(null);
    setSelectedFilePath(null);

    const newTask: GenerationTask = {
      id: Date.now().toString(),
      name: suggestion.name,
      topic: 'Planning & generating with pipeline…',
      progress: 0,
      status: 'building',
      timestamp: new Date(),
    };
    setActiveTask(newTask);
    setIsGenerating(true);
    setGenerationProgress(0);
    setBuildingLogs([]);
    setGenerationError(null);

    try {
      const planParams = new URLSearchParams({
        tab_ids: tabIds.join(','),
        app_type: suggestion.appType,
        app_name: suggestion.name,
        description: suggestion.description,
      });
      const token = useAuthStore.getState().tokens?.token;
      if (token) planParams.append('token', token);

      const planEs = new EventSource(
        `/api/v1/sessions/${workspaceSessionId}/apps/plan/stream?${planParams.toString()}`
      );
      (window as unknown as Record<string, EventSource | undefined>)['__appPlanSrc'] = planEs;

      await new Promise<void>((resolve, reject) => {
        planEs.addEventListener('hitl_prompt', (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.question && !data.user_instruction) {
              data.user_instruction = data.question;
            }
            setActiveHitlPrompt(data);
          } catch { /* ignore */ }
        });
        
        planEs.addEventListener('planning_progress', (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.stage !== 'error') bumpProgress(data.percent ?? data.progress);
          if (data.stage === 'error') appendLog(`✗ ${data.message}`);
          // Khiimori's own pipeline stages (fetching tabs, saving, S3 …); Genesis
          // steps arrive separately as 'building' events.
          else if (data.stage !== 'building' && data.message) appendLog(`• ${data.message}`);
          } catch { /* ignore */ }
        });
        
        planEs.addEventListener('building', (e) => {
          try {
            const data = JSON.parse(e.data);
            bumpProgress(data.percent);
            appendLog(data.message);
          } catch { /* ignore */ }
        });
        
        planEs.addEventListener('plan_ready', (e) => {
          try {
            const data = JSON.parse(e.data);
            const plan = data.plan ?? data;
            setAppPlan(plan);
            if (plan.files) {
              const parsedFiles = Object.entries(plan.files || {}).map(([path, content]) => ({ path, content: content as string, language: 'unknown', description: '', category: 'unknown' }));
              setGeneratedFiles(parsedFiles);
            }
            setPlanStage('review');
            setPipelineStage('planning');
            setActiveTask((prev) => prev ? { ...prev, progress: 25 } : null);
            setGenerationProgress(25);
            setIsGenerating(false);
          } catch { /* ignore */ }
          planEs.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appPlanSrc'];
          resolve();
        });

        planEs.addEventListener('plan_error', (e) => {
          try {
            const data = JSON.parse(e.data);
            toast.error(data.error ?? 'Planning failed');
          } catch {
            toast.error('Planning failed');
          }
          planEs.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appPlanSrc'];
          setPlanStage('none');
          setPipelineStage('idle');
          setActiveTask(null);
          setIsGenerating(false);
          reject(new Error('Planning failed'));
        });

        planEs.onerror = () => {
          planEs.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appPlanSrc'];
          setPlanStage('none');
          setPipelineStage('idle');
          setActiveTask(null);
          setIsGenerating(false);
          reject(new Error('Planning connection lost'));
        };
      });
    } catch (e) {
      if ((e as Error).message !== 'Planning failed' && (e as Error).message !== 'Planning connection lost') {
        setPlanStage('none');
        setPipelineStage('idle');
        setActiveTask(null);
        setIsGenerating(false);
      }
    }
  };

  const approvePlan = async () => {
    if (!appPlan || !workspaceSessionId) return;
    setPlanStage('approved');
    setPipelineStage('generating');
    setGenerationProgress(25);

    try {
      const tabIds = openTabs.map((t) => t.id);
      const genParams = new URLSearchParams({
        tab_ids: tabIds.join(','),
        plan_id: appPlan.id,
      });
      const token = useAuthStore.getState().tokens?.token;
      if (token) genParams.append('token', token);

      const genEs = new EventSource(
        `/api/v1/sessions/${workspaceSessionId}/apps/generate-multi/stream?${genParams.toString()}`
      );
      (window as unknown as Record<string, EventSource | undefined>)['__appGenMultiSrc'] = genEs;

      await new Promise<void>((resolve, reject) => {
        genEs.addEventListener('hitl_prompt', (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.question && !data.user_instruction) {
              data.user_instruction = data.question;
            }
            setActiveHitlPrompt(data);
          } catch { /* ignore */ }
        });

        genEs.addEventListener('file_generated', (e) => {
          try {
            const data = JSON.parse(e.data) as GeneratedFile;
            setGeneratedFiles((prev) => [...prev, data]);
            setGenerationProgress((prev) => Math.min(prev + 2, 70));
          } catch { /* ignore */ }
        });

        genEs.addEventListener('building', (e) => {
          try {
            const data = JSON.parse(e.data);
            bumpProgress(data.percent);
            appendLog(data.message);
          } catch { /* ignore parse errors */ }
        });

        genEs.addEventListener('generation_progress', (e) => {
          try {
            const data = JSON.parse(e.data);
            const pct = data.percent ?? data.progress;
            if (pct !== undefined) {
              bumpProgress(25 + (pct / 100) * 75);
            }
          } catch { /* ignore */ }
        });

        genEs.addEventListener('generation_completed', (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.preview_url) {
              setPreviewUrl(data.preview_url);
            }
          } catch { /* ignore */ }
          finalizePipeline();
          genEs.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appGenMultiSrc'];
          resolve();
        });

        genEs.addEventListener('critiquing_progress', (e) => {
          try {
            const data = JSON.parse(e.data);
            appendLog(data.message || 'Critiquing...');
          } catch { /* ignore */ }
        });

        genEs.addEventListener('critique_ready', (e) => {
          try {
            const data = JSON.parse(e.data);
            setCritiqueResult(data.critique ?? data);
            setGenerationProgress(90);
            if ((data.critique ?? data).would_auto_fix) {
              setPipelineStage('fixing');
            } else {
              finalizePipeline();
            }
          } catch { /* ignore */ }
          genEs.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appGenMultiSrc'];
          resolve();
        });

        genEs.addEventListener('generation_failed', (e) => {
          try {
            const data = JSON.parse(e.data);
            toast.error(data.error ?? 'Generation failed');
          } catch {
            toast.error('Generation failed');
          }
          genEs.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appGenMultiSrc'];
          setPipelineStage('idle');
          setActiveTask(null);
          setIsGenerating(false);
          reject(new Error('Generation failed'));
        });

        genEs.onerror = () => {
          genEs.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appGenMultiSrc'];
          // If we were critiquing, maybe it just closed naturally. But let's finalize to be safe.
          if (pipelineStageRef.current === 'critiquing') {
            finalizePipeline();
            resolve();
          } else {
            setPipelineStage('idle');
            setActiveTask(null);
            setIsGenerating(false);
            reject(new Error('Generation connection lost'));
          }
        };
      });
    } catch {
      // errors already handled in listeners
    }
  };

  const finalizePipeline = () => {
    setPipelineStage('complete');
    setGenerationProgress(100);
    setActiveTask((prev) =>
      prev ? { ...prev, progress: 100, status: 'ready' } : null,
    );
    const successMessage: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `Your workspace has been generated with the multi-step pipeline. Review the files and critique below.`,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, successMessage]);
    setTimeout(() => {
      setActiveTask((prev) => {
        if (prev) {
          setCompletedTasks((c) => [{ ...prev, status: 'complete' }, ...c]);
        }
        return null;
      });
      setIsGenerating(false);
      setGenerationProgress(0);
    }, 2000);
  };

  const handleDeployToCDN = async () => {
    const targetAppId = activeTask?.appId || completedTasks[0]?.appId;
    if (!targetAppId) {
      toast.error('No generated app to deploy yet');
      return;
    }
    setDeployStatus('deploying');
    try {
      const result = await appService.deployApp(targetAppId);
      setCdnUrl(result.cdn_url);
      setDeployStatus('done');
      toast.success('Deployed to CDN');
    } catch (err) {
      setDeployStatus('error');
      const message = err instanceof Error ? err.message : 'Deploy failed';
      toast.error(message);
    }
  };

  const handleAutoFix = async () => {
    if (!critiqueResult || !appPlan || !workspaceSessionId) return;
    setPipelineStage('fixing');
    setGenerationProgress(90);

    try {
      const token = useAuthStore.getState().tokens?.token || '';
      const tokenQ = token ? `&token=${encodeURIComponent(token)}` : '';
      const fixEs = new EventSource(
        `/api/v1/sessions/${workspaceSessionId}/apps/fix/stream?plan_id=${appPlan.id}${tokenQ}`
      );
      (window as unknown as Record<string, EventSource | undefined>)['__appFixSrc'] = fixEs;

      await new Promise<void>((resolve) => {
        fixEs.addEventListener('fix_progress', (e) => {
          try {
            const data = JSON.parse(e.data);
            setGenerationProgress(90 + ((data.percent ?? 0) / 100) * 10);
          } catch { /* ignore */ }
        });

        fixEs.addEventListener('fix_complete', (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.updated_files) {
              setGeneratedFiles(data.updated_files);
            }
          } catch { /* ignore */ }
          fixEs.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appFixSrc'];
          finalizePipeline();
          resolve();
        });

        fixEs.addEventListener('fix_error', () => {
          fixEs.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appFixSrc'];
          finalizePipeline();
          resolve();
        });

        fixEs.onerror = () => {
          fixEs.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appFixSrc'];
          finalizePipeline();
          resolve();
        };
      });
    } catch {
      finalizePipeline();
    }
  };

  // Launch a sandboxed preview for the most-recently-generated app and stream
  // the OpenSandbox progress into the existing `previewUrl` state. The
  // iframe below will switch to the resolved public endpoint as soon as it
  // arrives, falling back to the previous Vite preview if OpenSandbox is
  // unavailable.
  const startSandboxPreview = useCallback(async (appId: string) => {
    setPreviewUrl(null);
    try {
      const token = useAuthStore.getState().tokens?.token || '';
      const tokenQ = token ? `?token=${encodeURIComponent(token)}` : '';
      const es = new EventSource(`/api/v1/apps/${appId}/sandbox/create${tokenQ}`);
      (window as unknown as Record<string, EventSource | undefined>)['__appSandboxSrc'] = es;

      await new Promise<void>((resolve) => {
        const cleanup = () => {
          es.close();
          delete (window as unknown as Record<string, EventSource | undefined>)['__appSandboxSrc'];
          resolve();
        };
        es.addEventListener('progress', () => {
          // progress event is informational; the final `ready` event carries the URL.
        });
        es.addEventListener('ready', (e) => {
          try {
            const data = JSON.parse((e as MessageEvent).data);
            if (data.preview_url) {
              setPreviewUrl(String(data.preview_url));
            }
          } catch {
            /* ignore */
          }
          cleanup();
        });
        es.addEventListener('error', (e) => {
          try {
            const data = JSON.parse((e as MessageEvent).data);
            toast.error(data.error ?? 'Sandbox preview failed');
          } catch {
            /* ignore */
          }
          cleanup();
        });
        es.onerror = () => cleanup();
      });
    } catch (err) {
      console.error('Failed to start sandbox preview', err);
      setPreviewUrl(null);
    }
  }, []);

  const editPlanPrompt = () => {
    setPlanStage('none');
    setAppPlan(null);
    setPipelineStage('idle');
    setInputValue('Please revise the plan: ');
  };

  const regeneratePlan = () => {
    setPlanStage('planning');
    setPipelineStage('planning');
    setAppPlan(null);
    setTimeout(() => {
      const lastSuggestion = messages.find((m) => m.suggestion)?.suggestion;
      if (lastSuggestion) {
        void handleGenerateWithPlanning(lastSuggestion);
      }
    }, 500);
  };

  const getPipelineStepStatus = (step: string) => {
    const stages = ['planning', 'generating', 'critiquing', 'fixing'];
    const stageIndex = stages.indexOf(step);
    const currentIndex = stages.indexOf(pipelineStage);
    if (pipelineStage === 'idle') return 'pending';
    if (pipelineStage === 'complete') return 'completed';
    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) return 'active';
    return 'pending';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-400 bg-red-500/20 border-red-500/30';
      case 'major': return 'text-orange-400 bg-orange-500/20 border-orange-500/30';
      case 'minor': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'suggestion': return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
      default: return 'text-khiimori-text-muted bg-khiimori-bg-elevated/10 border-khiimori-border/20';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-success-400 bg-success-500/20';
    if (score >= 5) return 'text-amber-400 bg-amber-500/20';
    return 'text-red-400 bg-red-500/20';
  };

  const groupedFiles = generatedFiles.reduce<Record<string, GeneratedFile[]>>((acc, f) => {
    const category = f.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(f);
    return acc;
  }, {});

  const quickActions = [
    { label: 'Compare Products', icon: '⚖️', prompt: 'Compare these products side by side', description: 'Comparison workspace' },
    { label: 'Summarize Research', icon: '📚', prompt: 'Summarize all my research tabs', description: 'Research synthesis' },
    { label: 'Create Timeline', icon: '📅', prompt: 'Create a visual timeline from my tabs', description: 'Timeline-style app' },
    { label: 'Build Dashboard', icon: '📊', prompt: 'Build a custom dashboard', description: 'Dashboard workspace' },
  ];

  if (sessionLoading) {
    return (
      <div className="min-h-screen p-12 flex items-center justify-center text-khiimori-text-secondary">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!workspaceSessionId) {
    return (
      <div className="min-h-screen p-12 text-khiimori-text-secondary">
        <p className="mb-4">Sign in to use App Forge and load your workspace session.</p>
        <Link to="/login" className="text-primary-400 underline">
          Go to login
        </Link>
      </div>
    );
  }

  return (
    <PageLayout title="App Forge" description="Generate custom workspaces from your tabs with AI" icon={Wand2}>
      <div className="flex flex-col md:flex-row h-full bg-[#0f111a] text-khiimori-text-primary overflow-hidden relative font-sans -mx-6 -mt-6">
        {!activeTask && planStage === 'none' && messages.length > 0 && messages[0].suggestions && messages[0].suggestions.length > 0 ? (
          <div className="flex-1 w-full h-full overflow-y-auto p-8 relative z-10 flex items-center justify-center">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-900/10 via-[#0f111a]/50 to-[#0f111a] z-0" />
            <div className="relative z-10 w-full max-w-6xl">
              <div className="mt-12 flex flex-col items-center w-full pb-8">
                <h2 className="text-4xl font-bold text-white mb-4 tracking-tight">App Suggestions</h2>
                <p className="text-lg text-khiimori-text-muted mb-12 text-center max-w-2xl leading-relaxed">
                  Based on your recent browser activity, Khiimori AI has identified several potential workflows. Select an app to initialize a dedicated workspace.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                  {messages[0].suggestions.map((suggestion, i) => (
                    <motion.div
                      key={suggestion.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="flex flex-col bg-[#131722]/80 backdrop-blur-xl border border-[#1f2330] rounded-2xl p-6 transition-all hover:border-primary-500/50 hover:shadow-[0_0_30px_-5px_rgba(139,92,246,0.3)] group"
                    >
                      <div className="flex items-center justify-between mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1f2438] to-[#131722] border border-[#2a2e3d] flex items-center justify-center filter drop-shadow text-primary-400 group-hover:scale-110 transition-transform relative overflow-hidden">
                          {suggestion.icon}
                        </div>
                        <div className="px-3 py-1.5 rounded-lg bg-primary-500/10 border border-primary-500/20 text-xs font-mono font-bold tracking-wider text-primary-400">
                          {suggestion.confidence}% MATCH
                        </div>
                      </div>
                      
                      <h3 className="text-2xl font-bold text-white mb-3 tracking-tight leading-tight">{suggestion.name}</h3>
                      
                      <p className="text-sm text-khiimori-text-muted leading-relaxed mb-8 flex-1">
                        {suggestion.description}
                      </p>
                      
                      <div className="flex items-center gap-3 mt-auto">
                        <button
                          type="button"
                          onClick={() => void handleGenerateApp(suggestion)}
                          disabled={isGenerating}
                          className={`flex-1 py-3 px-5 rounded-xl font-bold flex items-center justify-center gap-2 transition-all text-sm border ${
                            i === 0 
                              ? 'bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] border-transparent text-white shadow-[0_0_15px_-3px_rgba(139,92,246,0.5)] hover:opacity-90' 
                              : 'bg-[#1f2438] border-transparent text-white hover:bg-[#2a2e3d]'
                          } disabled:opacity-50`}
                        >
                          {isGenerating && activeTask?.name === suggestion.name ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Zap className="w-4 h-4 fill-current" />
                          )}
                          Initialize
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleSaveWorkspace(suggestion)}
                          title="Bookmark Workspace"
                          className="p-3 rounded-xl bg-transparent text-khiimori-text-muted hover:text-white transition-all border border-[#2a2e3d] hover:bg-[#1f2438] hover:border-[#3b3f51]"
                        >
                          <Bookmark className="w-5 h-5" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : planStage === 'review' && appPlan ? (
          <>
            <div className="flex-1 flex flex-col relative bg-[#0f111a] border-r border-[#1f2330] z-10 w-full md:w-auto h-[calc(100vh-100px)]">
              <div className="flex-1 overflow-y-auto p-8 relative z-10 flex items-center justify-center">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-900/10 via-[#0f111a]/50 to-[#0f111a] z-0" />
                <div className="relative z-10 w-full max-w-4xl">
                  <div className="bg-[#131722]/90 backdrop-blur-xl border border-[#1f2330] rounded-2xl p-8 shadow-2xl animate-fade-in">
                    <div className="flex items-center gap-4 mb-6 pb-6 border-b border-[#1f2330]">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600 to-primary-900 flex items-center justify-center shadow-[0_0_20px_rgba(139,92,246,0.3)]">
                        <GanttChart className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">{appPlan.app_name}</h2>
                        <p className="text-khiimori-text-muted mt-1 text-sm">Architecture Plan Review</p>
                      </div>
                    </div>

                    <div className="space-y-8">
                      <div>
                        <h3 className="text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">Overview</h3>
                        <p className="text-gray-300 leading-relaxed text-sm bg-black/20 p-4 rounded-xl border border-white/5">{appPlan.summary}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <Layers className="w-4 h-4" /> Core Features
                          </h3>
                          <ul className="space-y-2">
                            {appPlan.core_features?.map((feature, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                                <CheckCircle2 className="w-4 h-4 text-success-500 mt-0.5 flex-shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <Braces className="w-4 h-4" /> Stack
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {appPlan.programming_languages?.map((lang, idx) => (
                              <span key={`lang-${idx}`} className="px-3 py-1.5 rounded-lg bg-[#1f2438] border border-[#2a2e3d] text-xs font-mono text-gray-300">
                                {lang}
                              </span>
                            ))}
                            {appPlan.dependencies?.map((dep, idx) => (
                              <span key={`dep-${idx}`} className="px-3 py-1.5 rounded-lg bg-[#1f2438] border border-[#2a2e3d] text-xs font-mono text-gray-300">
                                {dep}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Blocks className="w-4 h-4" /> Component Architecture
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {appPlan.component_tree?.map((comp, idx) => (
                            <div key={idx} className="bg-black/20 p-4 rounded-xl border border-white/5 flex flex-col gap-2">
                              <div className="flex items-center justify-between">
                                <span className="font-mono text-sm font-bold text-gray-200">{comp.name}</span>
                                <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-white/10 text-gray-400 tracking-wider">{comp.type}</span>
                              </div>
                              <p className="text-xs text-gray-500 leading-relaxed">{comp.purpose}</p>
                              {comp.children && comp.children.length > 0 && (
                                <div className="mt-2 text-xs text-gray-600 flex flex-wrap gap-1">
                                  <span className="text-gray-500 mr-1">Contains:</span>
                                  {comp.children.map((child, cIdx) => (
                                    <span key={cIdx}>{child}{cIdx < comp.children.length - 1 ? ',' : ''}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-4 pt-6 border-t border-[#1f2330]">
                        <button
                          type="button"
                          onClick={() => {
                            setPlanStage('none');
                            setPipelineStage('idle');
                            setActiveTask(null);
                            setIsGenerating(false);
                          }}
                          className="px-4 py-2 bg-[#2a2e3d] hover:bg-[#323644] text-gray-300 text-sm font-medium rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => void approvePlan()}
                          disabled={isGenerating}
                          className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] transition-all font-bold flex items-center gap-2"
                        >
                          <Play className="w-4 h-4" />
                          Approve & Generate App
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
          <div className="flex-1 flex flex-col relative bg-[#0f111a] border-r border-[#1f2330] z-10 w-full md:w-auto h-[calc(100vh-100px)]">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-900/10 via-[#0f111a]/50 to-[#0f111a] z-0" />
            <div className="flex-1 flex flex-col items-center justify-center p-8 relative z-10">
              {pipelineStage === 'complete' ? (
                <div className="flex flex-col items-center animate-fade-in text-center w-full h-full">
                  {previewUrl ? (
                    <div className="w-full h-full flex flex-col items-center">
                      <div className="flex items-center gap-4 mb-4">
                        <CheckCircle2 className="w-8 h-8 text-success-400" />
                        <h2 className="text-2xl font-bold text-white">Workspace Ready</h2>
                        <a href={previewUrl} target="_blank" rel="noreferrer" className="ml-4 text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1">
                          <ExternalLink className="w-4 h-4" /> Open in new tab
                        </a>
                        <button
                          onClick={handleDeployToCDN}
                          disabled={deployStatus === 'deploying'}
                          className="ml-4 text-sm text-primary-400 hover:text-primary-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          {deployStatus === 'deploying' ? 'Deploying…' : deployStatus === 'done' ? 'Redeploy to CDN' : 'Deploy to CDN'}
                        </button>
                      </div>
                      {cdnUrl && (
                        <a href={cdnUrl} target="_blank" rel="noreferrer" className="mb-4 text-sm text-success-400 hover:text-success-300 flex items-center gap-1">
                          <ExternalLink className="w-4 h-4" /> Live on CDN: {cdnUrl}
                        </a>
                      )}
                      <div className="w-full flex-1 rounded-xl overflow-hidden border border-[#2a2e3d] shadow-[0_0_30px_rgba(139,92,246,0.15)] bg-white min-h-[500px]">
                        <iframe src={previewUrl} className="w-full h-full border-0" title="Generated App Preview" />
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-lg">
                      <div className="w-24 h-24 rounded-full bg-success-500/20 border-2 border-success-500 flex items-center justify-center mb-6 mx-auto shadow-[0_0_40px_rgba(34,197,94,0.3)]">
                        <CheckCircle2 className="w-12 h-12 text-success-400" />
                      </div>
                      <h2 className="text-3xl font-bold text-white mb-4">Workspace Ready</h2>
                      <p className="text-gray-400 mb-8">
                        Your application has been successfully generated and is ready to use.
                        {' '}A live sandbox preview wasn't available for this run — deploy to the CDN for a public link instead.
                      </p>
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => {
                            const targetAppId = activeTask?.appId || completedTasks[0]?.appId;
                            if (targetAppId) {
                              navigate(`/apps/${targetAppId}`);
                            }
                          }}
                          className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] text-white font-bold text-lg shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] transition-all flex items-center gap-2"
                        >
                          <Eye className="w-5 h-5" /> View Code & Components
                        </button>
                        <button
                          onClick={handleDeployToCDN}
                          disabled={deployStatus === 'deploying'}
                          className="px-8 py-4 rounded-xl border border-[#2a2e3d] text-white font-bold text-lg hover:border-primary-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                        >
                          {deployStatus === 'deploying' ? 'Deploying…' : 'Deploy to CDN'}
                        </button>
                      </div>
                      {cdnUrl && (
                        <a href={cdnUrl} target="_blank" rel="noreferrer" className="mt-4 text-sm text-success-400 hover:text-success-300 flex items-center justify-center gap-1">
                          <ExternalLink className="w-4 h-4" /> Live on CDN: {cdnUrl}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ) : activeHitlPrompt ? (
                <HitlPromptCard
                  key={activeHitlPrompt.resume_token ?? activeHitlPrompt.token ?? 'hitl'}
                  prompt={activeHitlPrompt}
                  onSubmit={async (optionId, decision, reply) => {
                    try {
                      await api.post(`/sessions/${workspaceSessionId}/apps/generate/hitl-reply`, { reply, decision, option_id: optionId });
                      setActiveHitlPrompt(null);
                    } catch {
                      toast.error('Failed to submit reply');
                    }
                  }}
                />
              ) : (
                <BuildConsole
                  logs={buildingLogs}
                  progress={generationProgress}
                  error={generationError}
                  running={isGenerating && !generationError}
                />
              )}
            </div>
          </div>
          <aside className="w-full md:w-[320px] flex flex-col bg-[#0c0e15] z-10 border-l border-[#1f2330] h-[calc(100vh-100px)]">
            <div className="flex items-center justify-between px-4 h-12 border-b border-[#1f2330] bg-[#11141d]">
              <h3 className="font-semibold text-white text-sm">Deliverables</h3>
              {generatedFiles.length > 0 && (
                <span className="text-[11px] text-gray-500 tabular-nums">
                  {generatedFiles.length} files · {(generatedFiles.reduce((n, f) => n + f.content.length, 0) / 1024).toFixed(1)} KB
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-2 custom-scrollbar">
              <DeliverablesTree files={generatedFiles} onOpen={setSelectedFilePath} selectedPath={selectedFilePath} />
            </div>
            {pipelineStage === 'complete' && previewUrl && (
              <div className="p-4 border-t border-[#1f2330]">
                <button onClick={() => window.open(previewUrl)} className="w-full py-2 bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2 transition-all">
                  <Play className="w-4 h-4 fill-current" /> Open Sandbox
                </button>
              </div>
            )}
          </aside>
          </>
        )}
      </div>

      {selectedFilePath && (
        <FileViewerModal
          files={generatedFiles}
          selectedPath={selectedFilePath}
          onSelect={setSelectedFilePath}
          onClose={() => setSelectedFilePath(null)}
        />
      )}
    </PageLayout>
  );
}
